import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { displayErrorMessage, displaySuccessMessage } from 'redux/notificationBar/notificationBarSlice';
import { getIncident, addIncidentNote, updateIncidentStatus, dispatchIncident } from 'api/canvms/incidents';
import type { IncidentDetail, IncidentStatus, DispatchIncidentInput } from '../types/canvision.types';

interface UseIncidentDetailOptions {
  incidentId: number | null;
  enabled?: boolean;
}

export function useIncidentDetail({ incidentId, enabled = true }: UseIncidentDetailOptions) {
  const dispatch = useDispatch();
  const [incident, setIncident] = useState<IncidentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialLoadDone = useRef(false);

  const fetchIncident = useCallback(async (isRefresh = false) => {
    if (!incidentId) {
      setIncident(null);
      return;
    }

    try {
      if (!isRefresh && !initialLoadDone.current) {
        setLoading(true);
      }
      setError(null);
      const response = await getIncident(incidentId);
      setIncident(response.data);
      initialLoadDone.current = true;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to fetch incident';
      console.error('[CANVMS/fetchIncidentDetail] Error fetching incident:', err);
      setError(message);
      dispatch(displayErrorMessage(message));
    } finally {
      setLoading(false);
    }
  }, [incidentId, dispatch]);

  useEffect(() => {
    if (enabled && incidentId) {
      initialLoadDone.current = false;
      fetchIncident(false);
    } else {
      setIncident(null);
      initialLoadDone.current = false;
    }
  }, [incidentId, enabled, fetchIncident]);

  const updateStatus = useCallback(async (status: IncidentStatus, notes?: string) => {
    if (!incidentId) return;

    try {
      await updateIncidentStatus(incidentId, status, notes);
      await fetchIncident(true);
      dispatch(displaySuccessMessage(`Incident status updated to ${status}`));
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to update incident status';
      console.error('[CANVMS/updateIncidentDetailStatus] Error updating incident status:', err);
      dispatch(displayErrorMessage(message));
      throw err;
    }
  }, [incidentId, fetchIncident, dispatch]);

  const addNote = useCallback(async (note: string) => {
    if (!incidentId) return;

    try {
      await addIncidentNote(incidentId, note);
      await fetchIncident(true);
      dispatch(displaySuccessMessage('Note added successfully'));
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to add note';
      console.error('[CANVMS/addIncidentNote] Error adding note:', err);
      dispatch(displayErrorMessage(message));
      throw err;
    }
  }, [incidentId, fetchIncident, dispatch]);

  const dispatchPatrol = useCallback(async (input: DispatchIncidentInput) => {
    if (!incidentId) return;

    try {
      const response = await dispatchIncident(incidentId, input);
      await fetchIncident(true);
      dispatch(displaySuccessMessage('Patrol dispatched successfully'));
      return response.data;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to dispatch patrol';
      console.error('[CANVMS/dispatchIncident] Error dispatching incident:', err);
      dispatch(displayErrorMessage(message));
      throw err;
    }
  }, [incidentId, fetchIncident, dispatch]);

  const refetch = useCallback(() => fetchIncident(true), [fetchIncident]);

  return {
    incident,
    loading,
    error,
    updateStatus,
    addNote,
    dispatch: dispatchPatrol,
    refetch,
  };
}
